/** Builds the control panel DOM and keeps its read-outs in sync with state. */

import type { CanvasView } from "./canvas.js";
import type { Quality, State, ThemeChoice } from "./state.js";
import { TARGET_PRESETS, MECHANISM_PRESETS } from "./presets.js";
import { analyze } from "./analyze.js";
import { mechanismToJSON, mechanismToSVG, download } from "./exporters.js";

export interface UIHost {
  state: State;
  view: CanvasView;
  mutateState(fn: (s: State) => void): void;
  loadMechanismPreset(id: string): void;
  loadTargetPreset(id: string): void;
  clearTarget(): void;
  enterDrawMode(): void;
  startSynthesis(): void;
  stopSynthesis(): void;
  acceptResult(): void;
  requestRender(): void;
  applyTheme(): void;
}

const h = (html: string): HTMLElement => {
  const t = document.createElement("template");
  t.innerHTML = html.trim();
  return t.content.firstElementChild as HTMLElement;
};

const fmt = (x: number, d = 3): string =>
  Math.abs(x) < 1e-9 ? "0" : x.toFixed(d);

export function buildUI(root: HTMLElement, host: UIHost): (s: State) => void {
  root.innerHTML = "";

  const panel = h(`
    <div class="panel-inner">
      <section>
        <h2>Target curve</h2>
        <div class="row">
          <button data-act="draw" class="btn primary">✎ Draw target</button>
          <button data-act="clear" class="btn">Clear</button>
        </div>
        <div class="chips" data-grid="targets"></div>
      </section>

      <section>
        <h2>Mechanism</h2>
        <div class="seg" data-seg="topology">
          <button data-val="fourbar">Four-bar</button>
          <button data-val="stephenson3">Six-bar</button>
        </div>
        <div class="chips" data-grid="mechs"></div>
      </section>

      <section>
        <h2>Synthesize</h2>
        <div class="seg" data-seg="quality">
          <button data-val="fast">Fast</button>
          <button data-val="balanced">Balanced</button>
          <button data-val="thorough">Thorough</button>
        </div>
        <label class="check"><input type="checkbox" data-check="scale"> Allow rescaling the drawing</label>
        <div class="row">
          <label class="seed">seed
            <input type="number" data-input="seed" min="0" step="1">
          </label>
          <button data-act="shuffle" class="btn">🎲</button>
        </div>
        <button data-act="synth" class="btn go">Synthesize ▶</button>
        <div class="progress" data-progress hidden>
          <div class="pbar"><span data-pbar></span></div>
          <div class="pmeta" data-pmeta></div>
          <canvas data-spark width="260" height="40"></canvas>
          <button data-act="accept" class="btn" hidden>Keep this result</button>
        </div>
      </section>

      <section>
        <h2>View</h2>
        <div class="row">
          <button data-act="play" class="btn"></button>
          <button data-act="fit" class="btn">Fit</button>
          <label class="check"><input type="checkbox" data-check="labels"> Labels</label>
        </div>
        <label class="slider">speed <input type="range" data-input="speed" min="0" max="4" step="0.05"></label>
        <label class="slider">angle <input type="range" data-input="angle" min="0" max="6.2832" step="0.01"></label>
      </section>

      <section>
        <h2>Report</h2>
        <div class="report" data-report></div>
      </section>

      <section>
        <h2>Export</h2>
        <div class="row">
          <button data-act="json" class="btn">Copy JSON</button>
          <button data-act="svg" class="btn">Copy SVG</button>
        </div>
      </section>
    </div>
  `);
  root.appendChild(panel);

  // ---- populate preset chips ----
  const targetGrid = panel.querySelector('[data-grid="targets"]')!;
  for (const p of TARGET_PRESETS) {
    const chip = h(`<button class="chip" title="${p.hint}">${p.name}</button>`);
    chip.addEventListener("click", () => host.loadTargetPreset(p.id));
    targetGrid.appendChild(chip);
  }
  const mechGrid = panel.querySelector('[data-grid="mechs"]')!;
  for (const p of MECHANISM_PRESETS) {
    const chip = h(`<button class="chip" data-topo="${p.topology}" title="${p.hint}">${p.name}</button>`);
    chip.addEventListener("click", () => host.loadMechanismPreset(p.id));
    mechGrid.appendChild(chip);
  }

  // ---- wire controls ----
  const act = (name: string) => panel.querySelector(`[data-act="${name}"]`) as HTMLButtonElement;
  act("draw").addEventListener("click", () => host.enterDrawMode());
  act("clear").addEventListener("click", () => host.clearTarget());
  act("shuffle").addEventListener("click", () =>
    host.mutateState((s) => {
      s.seed = Math.floor(Math.random() * 100000);
    }),
  );
  act("synth").addEventListener("click", () =>
    host.state.synthRunning ? host.stopSynthesis() : host.startSynthesis(),
  );
  act("accept").addEventListener("click", () => host.acceptResult());
  act("play").addEventListener("click", () =>
    host.mutateState((s) => {
      s.playing = !s.playing;
    }),
  );
  act("fit").addEventListener("click", () => {
    const pts = [
      ...(host.state.trace ?? []),
      ...(host.state.target ?? []),
      ...host.state.spec.grounds,
    ];
    host.view.fit(pts, 0.35);
    host.requestRender();
  });
  act("json").addEventListener("click", async () => {
    const json = mechanismToJSON(host.state.spec, {
      matchError: host.state.result?.parts.chamfer,
    });
    try {
      await navigator.clipboard.writeText(json);
      flash(act("json"), "Copied ✓");
    } catch {
      download("mechanism.json", json, "application/json");
    }
  });
  act("svg").addEventListener("click", async () => {
    const svg = mechanismToSVG(host.state.spec, {
      target: host.state.target,
      driverAngle: host.state.driverAngle,
    });
    try {
      await navigator.clipboard.writeText(svg);
      flash(act("svg"), "SVG copied ✓");
    } catch {
      download("mechanism.svg", svg, "image/svg+xml");
    }
  });

  panel.querySelectorAll('[data-seg="topology"] button').forEach((b) =>
    b.addEventListener("click", () =>
      host.mutateState((s) => {
        s.topology = (b as HTMLElement).dataset.val as State["topology"];
      }),
    ),
  );
  panel.querySelectorAll('[data-seg="quality"] button').forEach((b) =>
    b.addEventListener("click", () =>
      host.mutateState((s) => {
        s.quality = (b as HTMLElement).dataset.val as Quality;
      }),
    ),
  );

  const scaleCheck = panel.querySelector('[data-check="scale"]') as HTMLInputElement;
  scaleCheck.addEventListener("change", () =>
    host.mutateState((s) => {
      s.allowScale = scaleCheck.checked;
    }),
  );
  const labelCheck = panel.querySelector('[data-check="labels"]') as HTMLInputElement;
  labelCheck.addEventListener("change", () =>
    host.mutateState((s) => {
      s.showLabels = labelCheck.checked;
    }),
  );
  const seedInput = panel.querySelector('[data-input="seed"]') as HTMLInputElement;
  seedInput.addEventListener("change", () =>
    host.mutateState((s) => {
      s.seed = Math.max(0, Math.floor(Number(seedInput.value) || 0));
    }),
  );
  const speedInput = panel.querySelector('[data-input="speed"]') as HTMLInputElement;
  speedInput.addEventListener("input", () =>
    host.mutateState((s) => {
      s.speed = Number(speedInput.value);
    }),
  );
  const angleInput = panel.querySelector('[data-input="angle"]') as HTMLInputElement;
  angleInput.addEventListener("input", () =>
    host.mutateState((s) => {
      s.playing = false;
      s.driverAngle = Number(angleInput.value);
    }),
  );

  // ---- sparkline history ----
  const spark = panel.querySelector("[data-spark]") as HTMLCanvasElement;
  const sparkData: number[] = [];
  let lastProgressKey = "";

  function drawSpark(): void {
    const c = spark.getContext("2d")!;
    const w = spark.width;
    const hgt = spark.height;
    c.clearRect(0, 0, w, hgt);
    if (sparkData.length < 2) return;
    const lo = Math.min(...sparkData);
    const hi = Math.max(...sparkData);
    const span = hi - lo || 1;
    c.strokeStyle = getComputedStyle(root).getPropertyValue("--accent") || "#1d4ed8";
    c.lineWidth = 1.5;
    c.beginPath();
    sparkData.forEach((v, i) => {
      const x = (w * i) / (sparkData.length - 1);
      const y = hgt - 3 - ((v - lo) / span) * (hgt - 6);
      if (i === 0) c.moveTo(x, y);
      else c.lineTo(x, y);
    });
    c.stroke();
  }

  // ---- update function ----
  const reportEl = panel.querySelector("[data-report]")!;
  const progressEl = panel.querySelector("[data-progress]") as HTMLElement;
  const pbar = panel.querySelector("[data-pbar]") as HTMLElement;
  const pmeta = panel.querySelector("[data-pmeta]") as HTMLElement;

  // analyze() runs a full trace; skip it when the mechanism hasn't changed.
  let reportSig = "";

  return function update(s: State): void {
    seedInput.value = String(s.seed);
    speedInput.value = String(s.speed);
    angleInput.value = String(s.driverAngle % (2 * Math.PI));
    scaleCheck.checked = s.allowScale;
    labelCheck.checked = s.showLabels;
    act("play").textContent = s.playing ? "⏸ Pause" : "▶ Play";
    act("draw").classList.toggle("active", s.mode === "draw");
    act("clear").disabled = !s.target;

    panel.querySelectorAll('[data-seg="topology"] button').forEach((b) =>
      b.classList.toggle("on", (b as HTMLElement).dataset.val === s.topology),
    );
    panel.querySelectorAll('[data-seg="quality"] button').forEach((b) =>
      b.classList.toggle("on", (b as HTMLElement).dataset.val === s.quality),
    );
    mechGrid.querySelectorAll(".chip").forEach((c) =>
      c.classList.toggle("dim", (c as HTMLElement).dataset.topo !== s.topology),
    );

    const synthBtn = act("synth");
    synthBtn.textContent = s.synthRunning ? "Stop ■" : "Synthesize ▶";
    synthBtn.classList.toggle("stop", s.synthRunning);
    synthBtn.disabled = !s.target && !s.synthRunning;

    // progress
    const showProg = s.synthRunning || !!s.progress;
    progressEl.hidden = !showProg;
    if (s.progress) {
      const p = s.progress;
      const totalUnits = p.restarts + 1;
      const doneUnits =
        p.phase === "polish" ? p.restarts + p.iter / Math.max(p.iters, 1) : p.restart + p.iter / Math.max(p.iters, 1);
      pbar.style.width = `${Math.min(100, (100 * doneUnits) / totalUnits).toFixed(1)}%`;
      pmeta.textContent =
        `${p.phase === "polish" ? "polishing" : `start ${p.restart + 1}/${p.restarts}`} · ` +
        `best ${p.bestLoss.toExponential(2)} · ${(p.convergedFraction * 100) | 0}% assembled`;
      const key = `${p.phase}-${p.restart}-${p.iter}`;
      if (key !== lastProgressKey) {
        lastProgressKey = key;
        sparkData.push(Math.log10(Math.max(p.bestLoss, 1e-12)));
        if (sparkData.length > 260) sparkData.shift();
        drawSpark();
      }
    }
    if (!s.synthRunning && !s.progress) sparkData.length = 0;
    (act("accept") as HTMLElement).hidden = !(s.result && !s.synthRunning);

    // report — skip the trace in analyze() when nothing relevant changed
    const sig = `${s.specVersion}:${!!s.result}`;
    if (sig === reportSig) return;
    reportSig = sig;
    const r = analyze(s.spec);
    const badge = (ok: boolean, txt: string) =>
      `<span class="badge ${ok ? "ok" : "warn"}">${txt}</span>`;
    reportEl.innerHTML = `
      <div class="badges">
        ${badge(r.grashof.isGrashof, r.grashof.isGrashof ? "Grashof" : "non-Grashof")}
        ${badge(r.grashof.crankRotates, r.grashof.crankRotates ? "crank rotates" : "crank rocks")}
        ${badge(r.convergedFraction > 0.999, `${(r.convergedFraction * 100) | 0}% assembled`)}
      </div>
      <table class="links">
        ${r.links.map((l) => `<tr><td>${l.name}</td><td>${fmt(l.length)}</td></tr>`).join("")}
      </table>
      <div class="metric">transmission angle:
        ${r.transmissionDeg ? `${r.transmissionDeg.min.toFixed(0)}°–${r.transmissionDeg.max.toFixed(0)}°` : "—"}
      </div>
      ${
        s.result
          ? `<div class="metric">match error: ${(Math.sqrt(Math.max(s.result.parts.chamfer, 0)) / (r.couplerBBox.w || 1) * 100).toFixed(1)}% of width</div>`
          : ""
      }
    `;
  };
}

function flash(btn: HTMLElement, text: string): void {
  const prev = btn.textContent;
  btn.textContent = text;
  setTimeout(() => {
    btn.textContent = prev;
  }, 1200);
}

export function themeIcon(t: ThemeChoice): string {
  return t === "dark" ? "☾" : t === "light" ? "☀" : "◐";
}
